import {
  AfterViewInit,
  Directive,
  DOCUMENT,
  inject,
  input,
} from '@angular/core';
import { Table } from 'primeng/table';
import { isNil, Nillable } from '@web/utils';

@Directive({
  selector: '[appTableTopContentScroll]',
  standalone: true,
})
export class TableTopContentScrollDirective implements AfterViewInit {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly table = inject(Table);

  public readonly topScrollEnabled = input<boolean>(false);

  public ngAfterViewInit(): void {
    if (!this.topScrollEnabled()) {
      return;
    }

    const containerHtmlRef = this.containerHtmlRef();
    const tableHtmlRef = this.tableHtmlRef();
    const wrapperHtmlRef = this.wrapperHtmlRef();

    if (
      isNil(containerHtmlRef) ||
      isNil(tableHtmlRef) ||
      isNil(wrapperHtmlRef)
    ) {
      return;
    }

    const scrollableContainer = this.document.createElement('div');
    const scrollableContent = this.document.createElement('div');
    const width = tableHtmlRef.scrollWidth;

    scrollableContainer.style.overflow = 'auto';
    scrollableContent.style.width = `${width}px`;
    scrollableContent.style.height = `${this.scrollbarWidth()}px`;
    scrollableContainer.appendChild(scrollableContent);

    containerHtmlRef.prepend(scrollableContainer);

    this.synchronizeScrolls(scrollableContainer, wrapperHtmlRef);
  }

  private tableHtmlRef(): Nillable<HTMLElement> {
    return this.table.tableViewChild?.nativeElement as Nillable<HTMLElement>;
  }

  private wrapperHtmlRef(): Nillable<HTMLElement> {
    return this.table.wrapperViewChild?.nativeElement as Nillable<HTMLElement>;
  }

  private containerHtmlRef(): Nillable<HTMLElement> {
    const wrapperHtmlRef = this.wrapperHtmlRef();

    return wrapperHtmlRef?.parentElement as Nillable<HTMLElement>;
  }

  private synchronizeScrolls(first: HTMLElement, second: HTMLElement): void {
    first.addEventListener('scroll', () => {
      second.scrollLeft = first.scrollLeft;
    });

    second.addEventListener('scroll', () => {
      first.scrollLeft = second.scrollLeft;
    });
  }

  private scrollbarWidth(): number {
    const outer = this.document.createElement('div');

    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    this.document.body.appendChild(outer);

    const inner = this.document.createElement('div');

    outer.appendChild(inner);

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

    outer.parentNode?.removeChild(outer);

    if (scrollbarWidth === 0) {
      return 16;
    }

    return scrollbarWidth;
  }
}
